import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import LabelIcon from '@mui/icons-material/Label';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useSnackbar} from 'notistack';
import TaskDialog from '../Tasks/TaskDialog';
import {TaskDTO} from '../../api';
import {AttributeDTO, LeadDTO} from '../../api/generated/requests/types.gen';
import {
    useAcquisitionTypesServiceGetAcquisitionTypes,
    useAttributesServiceDeleteApiAttributesById,
    useAttributesServiceDeleteApiAttributesObjectByObjectTypeByObjectId,
    useAttributesServiceGetApiAttributesObjectByObjectTypeByObjectId,
    useAttributesServicePostApiAttributesBulk,
    useAttributesServicePutApiAttributesBulk,
    useLeadAttributesServiceGetApiPredefinedLeadAttributes,
    useOffersServiceGetApiOffers
} from '../../api/generated/queries/queries';

interface LeadContactDialogProps {
    open: boolean;
    onClose: () => void;
    lead: LeadDTO | null;
    onSave: (lead: LeadDTO, createNext?: boolean, attributes?: AttributeDTO[]) => Promise<LeadDTO | void>;
    isSubmitting: boolean;
}

const initialValues: LeadDTO = {
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    alternatePhone: '',
    city: '',
    postalCode: '',
    street: '',
    houseNumber: '',
    apartmentNumber: '',
    nip: '',
    offer: undefined
};

const validationSchema = Yup.object({
    firstName: Yup.string()
        .required('Imię jest wymagane')
        .min(2, 'Imię musi mieć co najmniej 2 znaki')
        .max(50, 'Imię nie może być dłuższe niż 50 znaków'),
    lastName: Yup.string()
        .required('Nazwisko jest wymagane')
        .min(2, 'Nazwisko musi mieć co najmniej 2 znaki')
        .max(50, 'Nazwisko nie może być dłuższe niż 100 znaków'),
    email: Yup.string()
        .email('Niepoprawny format adresu email')
        .max(100, 'Email nie może być dłuższy niż 100 znaków')
        .nullable(),
    phone: Yup.string()
        .required('Numer telefonu jest wymagany')
        .matches(/^[+]?[0-9\s-]{9,15}$/, 'Niepoprawny format numeru telefonu (9-15 cyfr, dozwolone spacje, myślniki i znak +)'),
    alternatePhone: Yup.string()
        .matches(/^[+]?[0-9\s-]{9,15}$/, 'Niepoprawny format numeru telefonu (9-15 cyfr, dozwolone spacje, myślniki i znak +)')
        .nullable(),
    nip: Yup.string()
        .matches(/^[0-9]{10}$/, 'NIP musi składać się z dokładnie 10 cyfr')
        .nullable(),
    postalCode: Yup.string()
        .matches(/^[0-9]{2}-[0-9]{3}$/, 'Kod pocztowy musi być w formacie XX-XXX')
        .nullable(),
    name: Yup.string()
        .max(100, 'Nazwa firmy nie może być dłuższa niż 100 znaków')
        .nullable(),
    city: Yup.string()
        .max(50, 'Nazwa miasta nie może być dłuższa niż 50 znaków')
        .nullable(),
    street: Yup.string()
        .max(100, 'Nazwa ulicy nie może być dłuższa niż 100 znaków')
        .nullable(),
    houseNumber: Yup.string()
        .max(10, 'Numer domu nie może być dłuższy niż 10 znaków')
        .nullable(),
    apartmentNumber: Yup.string()
        .max(10, 'Numer mieszkania nie może być dłuższy niż 10 znaków')
        .nullable(),
});

const LeadContactDialog: React.FC<LeadContactDialogProps> = ({
                                                                 open,
                                                                 onClose,
                                                                 lead,
                                                                 onSave,
                                                                 isSubmitting
                                                             }) => {
    const [createNext, setCreateNext] = useState(false);
    const [createTask, setCreateTask] = useState(false);
    const [createdLead, setCreatedLead] = useState<LeadDTO | null>(null);
    const [showTaskDialog, setShowTaskDialog] = useState(false);
    const { data: acquisitionTypes, isLoading: isLoadingAcquisitionTypes } =
        useAcquisitionTypesServiceGetAcquisitionTypes();
    const { data: offers, isLoading: isLoadingOffers } = useOffersServiceGetApiOffers();
    
    // Attributes state and API calls
    const [attributes, setAttributes] = useState<Record<string, string>>({});
    const [attributeIds, setAttributeIds] = useState<Record<string, number>>({});
    const [selectedAttributeKeys, setSelectedAttributeKeys] = useState<Set<string>>(new Set());
    const [attributeMenuAnchor, setAttributeMenuAnchor] = useState<null | HTMLElement>(null);
    const { enqueueSnackbar } = useSnackbar();
    const { data: predefinedAttributes, isLoading: isLoadingPredefinedAttributes } = 
        useLeadAttributesServiceGetApiPredefinedLeadAttributes();
    const { data: existingAttributes, isLoading: isLoadingExistingAttributes } = 
        useAttributesServiceGetApiAttributesObjectByObjectTypeByObjectId(
            { objectType: 'LEAD', objectId: lead?.id || 0 },
            [],
            { enabled: !!lead?.id }
        );

    // Populate attributes state when existing attributes are loaded
    useEffect(() => {
        if (existingAttributes) {
            const attributesMap: Record<string, string> = {};
            const attributeIdsMap: Record<string, number> = {};
            const selectedKeys = new Set<string>();
            existingAttributes.forEach(attr => {
                if (attr.attributeKey && attr.attributeValue && attr.id) {
                    attributesMap[attr.attributeKey] = attr.attributeValue;
                    attributeIdsMap[attr.attributeKey] = attr.id;
                    selectedKeys.add(attr.attributeKey);
                }
            });
            setAttributes(attributesMap);
            setAttributeIds(attributeIdsMap);
            setSelectedAttributeKeys(selectedKeys);
        }
    }, [existingAttributes]);

    // Reset attributes when dialog opens to prevent persistence from previous sessions
    useEffect(() => {
        if (open) {
            setAttributes({});
            setAttributeIds({});
            setSelectedAttributeKeys(new Set());
        }
    }, [open]);

    // Mutation hooks for attribute operations
    const postAttributesBulk = useAttributesServicePostApiAttributesBulk();
    const putAttributesBulk = useAttributesServicePutApiAttributesBulk();
    const deleteAttribute = useAttributesServiceDeleteApiAttributesById();
    const deleteAttributesForObject = useAttributesServiceDeleteApiAttributesObjectByObjectTypeByObjectId();

    // Helper function to prepare attribute data
    const prepareAttributeData = (leadId: number): AttributeDTO[] => {
        return Object.entries(attributes)
            .filter(([key, value]) => key && value.trim() !== '')
            .map(([key, value]) => {
                // Find the attributeType from predefined attributes
                const predefinedAttr = predefinedAttributes?.find(attr => attr.attributeKey === key);
                const attributeType = predefinedAttr?.attributeType;

                return {
                    attributeKey: key,
                    attributeValue: value,
                    attributeType: attributeType,
                    relatedObjectId: leadId,
                    relatedObjectType: 'LEAD' as const
                };
            });
    };

    // Helper functions for attribute management
    const handleAddAttribute = (attributeKey: string) => {
        setSelectedAttributeKeys(prev => {
            const newSet = new Set(prev);
            newSet.add(attributeKey);
            return newSet;
        });
        setAttributes(prev => ({ ...prev, [attributeKey]: '' }));
        setAttributeMenuAnchor(null);
    };

    const handleRemoveAttribute = async (attributeKey: string) => {
        const attributeId = attributeIds[attributeKey];
        
        // If attribute has an ID, delete it from backend first
        if (attributeId) {
            try {
                await deleteAttribute.mutateAsync({ id: attributeId });
                enqueueSnackbar('Atrybut został usunięty', { variant: 'success' });
            } catch (error) {
                console.error('Error deleting attribute:', error);
                enqueueSnackbar('Błąd podczas usuwania atrybutu', { variant: 'error' });
                return; // Don't update local state if backend deletion failed
            }
        }
        
        // Update local state only after successful backend deletion (or if no ID exists)
        setSelectedAttributeKeys(prev => {
            const newSet = new Set(prev);
            newSet.delete(attributeKey);
            return newSet;
        });
        setAttributes(prev => {
            const newAttributes = { ...prev };
            delete newAttributes[attributeKey];
            return newAttributes;
        });
        setAttributeIds(prev => {
            const newAttributeIds = { ...prev };
            delete newAttributeIds[attributeKey];
            return newAttributeIds;
        });
    };

    const getAvailableAttributes = () => {
        return predefinedAttributes?.filter(attr => 
            attr.attributeKey && !selectedAttributeKeys.has(attr.attributeKey)
        ) || [];
    };

    const formik = useFormik({
        initialValues: lead || initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { resetForm }) => {
            try {
                if (!lead?.id) {
                    // For new leads, prepare attributes data and pass to onSave
                    // This eliminates duplicate API calls by letting NewLeadButton handle both contact creation and attribute saving
                    const attributeData = prepareAttributeData(0); // Use 0 as placeholder, will be replaced with actual leadId in NewLeadButton
                    
                    // Call onSave with contact data and attributes - NewLeadButton will handle the API calls
                    const result = await onSave(values, createNext, attributeData);
                    
                    // Handle post-creation logic
                    setCreatedLead(result || null);

                    // If "create task" checkbox is checked, open TaskDialog directly
                    if (createTask) {
                        setShowTaskDialog(true);
                    }

                    // Reset form when creating a new lead
                    resetForm();
                    setCreateNext(false); // Reset the checkbox as well
                    setCreateTask(false); // Reset the create task checkbox as well
                    setAttributes({}); // Reset attributes as well
                    setAttributeIds({}); // Reset attribute IDs as well
                    setSelectedAttributeKeys(new Set()); // Reset selected attribute keys as well
                } else {
                    // For existing leads, use the original flow
                    const result = await onSave(values, createNext);

                    // Handle attributes after lead is saved
                    const leadId = result?.id || lead?.id;
                    if (leadId) {
                        const attributeData = prepareAttributeData(leadId);
                        
                        if (attributeData.length > 0) {
                            // For existing leads, update attributes
                            await putAttributesBulk.mutateAsync({
                                requestBody: attributeData
                            });
                        }
                    }
                }
            } catch (error) {
                // Error handling is done in the parent component
                console.error('Error in form submission:', error);
                throw error; // Re-throw to let parent handle it
            }
        },
    });

    // Handler functions for task dialog
    const handleTaskDialogClose = () => {
        setShowTaskDialog(false);
        setCreatedLead(null);
    };

    const handleTaskSave = (task: TaskDTO) => {
        // Task saving is handled by TaskDialog internally
        setShowTaskDialog(false);
        setCreatedLead(null);
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { overflowY: 'visible', borderRadius: 2 } }}
        >
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle
                    sx={{
                        fontSize: '1rem', // zmniejszona czcionka (domyślnie jest ~1.25rem)
                        fontWeight: 600,  // subtelne pogrubienie
                    }}
                >
                    {lead?.id ? 'Edytuj kontakt' : 'Dodaj nowy kontakt'}
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{ backgroundColor: 'background.paper', borderRadius: 0 }}
                >
                    <Stack spacing={1.5} >
                        {/* Sekcja: Dane firmy */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <BusinessIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                                    Dane firmy
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    size="small"
                                    id="name"
                                    name="name"
                                    label="Nazwa firmy"
                                    value={formik.values.name || ''}
                                    onChange={formik.handleChange}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                    sx={{
                                        flex: 2,
                                        '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                    }}
                                />
                                <TextField
                                    size="small"
                                    id="nip"
                                    name="nip"
                                    label="NIP"
                                    value={formik.values.nip || ''}
                                    onChange={formik.handleChange}
                                    error={formik.touched.nip && Boolean(formik.errors.nip)}
                                    helperText={formik.touched.nip && formik.errors.nip}
                                    sx={{
                                        flex: 1,
                                        '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Sekcja: Dane osobowe */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PersonIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                                    Dane osobowe
                                </Typography>
                            </Box>

                            {/* Wiersz z imieniem i nazwiskiem */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                                <TextField
                                    size="small"
                                    id="firstName"
                                    name="firstName"
                                    label="Imię"
                                    value={formik.values.firstName || ''}
                                    onChange={formik.handleChange}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    required
                                    sx={{
                                        flex: 1,
                                        '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                    }}
                                />
                                <TextField
                                    size="small"
                                    id="lastName"
                                    name="lastName"
                                    label="Nazwisko"
                                    value={formik.values.lastName || ''}
                                    onChange={formik.handleChange}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    required
                                    sx={{
                                        flex: 1,
                                        '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                    }}
                                />
                            </Box>
                            <Stack spacing={1.5}>
                                {/* Wiersz z email, telefonem i telefonem dodatkowym */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        size="small"
                                        id="email"
                                        name="email"
                                        label="Email"
                                        type="email"
                                        value={formik.values.email || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        id="phone"
                                        name="phone"
                                        label="Telefon"
                                        value={formik.values.phone || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.phone && Boolean(formik.errors.phone)}
                                        helperText={formik.touched.phone && formik.errors.phone}
                                        required
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        id="alternatePhone"
                                        name="alternatePhone"
                                        label="Telefon dodatkowy"
                                        value={formik.values.alternatePhone || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.alternatePhone && Boolean(formik.errors.alternatePhone)}
                                        helperText={formik.touched.alternatePhone && formik.errors.alternatePhone}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        {/* Sekcja: Adres */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <HomeIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                                    Adres
                                </Typography>
                            </Box>

                            <Stack spacing={1.5}>
                                {/* Miasto + Kod pocztowy */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        size="small"
                                        id="city"
                                        name="city"
                                        label="Miasto"
                                        value={formik.values.city || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.city && Boolean(formik.errors.city)}
                                        helperText={formik.touched.city && formik.errors.city}
                                        sx={{
                                            flex: 2,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        id="postalCode"
                                        name="postalCode"
                                        label="Kod pocztowy"
                                        value={formik.values.postalCode || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
                                        helperText={formik.touched.postalCode && formik.errors.postalCode}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                </Box>

                                {/* Ulica + Numer domu + Numer mieszkania */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        size="small"
                                        id="street"
                                        name="street"
                                        label="Ulica"
                                        value={formik.values.street || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.street && Boolean(formik.errors.street)}
                                        helperText={formik.touched.street && formik.errors.street}
                                        sx={{
                                            flex: 2,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        id="houseNumber"
                                        name="houseNumber"
                                        label="Numer domu"
                                        value={formik.values.houseNumber || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.houseNumber && Boolean(formik.errors.houseNumber)}
                                        helperText={formik.touched.houseNumber && formik.errors.houseNumber}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        id="apartmentNumber"
                                        name="apartmentNumber"
                                        label="Numer mieszkania"
                                        value={formik.values.apartmentNumber || ''}
                                        onChange={formik.handleChange}
                                        error={formik.touched.apartmentNumber && Boolean(formik.errors.apartmentNumber)}
                                        helperText={formik.touched.apartmentNumber && formik.errors.apartmentNumber}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Box>


                        {/* Sekcja: Dodatkowe informacje */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LabelIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                                    Dodatkowe informacje
                                </Typography>
                            </Box>
                            
                            {/* Oferta i Źródło kontaktu */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                {/* Oferta */}
                                <FormControl variant="outlined" size="small"
                                             sx={{
                                                 flex: 1,
                                                 '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                                 '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                                                 '& .MuiSelect-select': { fontSize: '0.85rem' }
                                             }}>
                                    <InputLabel id="offer-label">Oferta</InputLabel>
                                    <Select
                                        labelId="offer-label"
                                        id="offer"
                                        name="offer"
                                        value={formik.values.offer || ''}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            formik.setFieldValue('offer', selectedId || undefined);
                                        }}
                                        label="Oferta"
                                        disabled={isLoadingOffers}
                                    >
                                        <MenuItem value="" sx={{ fontSize: '0.85rem' }}>Brak</MenuItem>
                                        {offers?.map((offer) => (
                                            <MenuItem key={offer.id} value={offer.id} sx={{ fontSize: '0.85rem' }}>
                                                {offer.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Źródło kontaktu */}
                                <FormControl variant="outlined" size="small"
                                             sx={{
                                                 flex: 1,
                                                 '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                                 '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                                                 '& .MuiSelect-select': { fontSize: '0.85rem' }
                                             }}>
                                    <InputLabel id="acquisition-type-label">Źródło kontaktu</InputLabel>
                                    <Select
                                        labelId="acquisition-type-label"
                                        id="acquisitionType"
                                        name="acquisitionType"
                                        value={formik.values.acquisitionType?.id || ''}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const selected = acquisitionTypes?.find((type) => type.id === selectedId);
                                            formik.setFieldValue('acquisitionType', selected || undefined);
                                        }}
                                        label="Źródło kontaktu"
                                        disabled={isLoadingAcquisitionTypes}
                                    >
                                        <MenuItem value="" sx={{ fontSize: '0.85rem' }}>Brak</MenuItem>
                                        {acquisitionTypes?.map((type) => (
                                            <MenuItem key={type.id} value={type.id} sx={{ fontSize: '0.85rem' }}>
                                                {type.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Atrybuty - nowa implementacja z przyciskiem + */}
                            <Box>
                                {/* Wyświetl wybrane atrybuty */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {Array.from(selectedAttributeKeys).map((attributeKey) => {
                                    const predefinedAttr = predefinedAttributes?.find(attr => attr.attributeKey === attributeKey);
                                    const attributeType = predefinedAttr?.attributeType;
                                    const currentValue = attributes[attributeKey] || '';

                                    const renderAttributeInput = () => {
                                        const commonSx = {
                                            '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                                            flex: 1
                                        };

                                        switch (attributeType) {
                                            case 'BOOLEAN':
                                                return (
                                                    <FormControl variant="outlined" size="small" sx={commonSx}>
                                                        <InputLabel id={`${attributeKey}-label`}>{attributeKey}</InputLabel>
                                                        <Select
                                                            labelId={`${attributeKey}-label`}
                                                            value={currentValue || ''}
                                                            onChange={(e) => {
                                                                setAttributes(prev => ({
                                                                    ...prev,
                                                                    [attributeKey]: e.target.value
                                                                }));
                                                            }}
                                                            label={attributeKey}
                                                            disabled={isLoadingPredefinedAttributes}
                                                            sx={{
                                                                '& .MuiSelect-select': { fontSize: '0.85rem' }
                                                            }}
                                                        >
                                                            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>Brak</MenuItem>
                                                            <MenuItem value="true" sx={{ fontSize: '0.85rem' }}>Tak</MenuItem>
                                                            <MenuItem value="false" sx={{ fontSize: '0.85rem' }}>Nie</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                );

                                            case 'DATE':
                                                return (
                                                    <TextField
                                                        size="small"
                                                        type="date"
                                                        label={attributeKey}
                                                        value={currentValue}
                                                        onChange={(e) => {
                                                            setAttributes(prev => ({
                                                                ...prev,
                                                                [attributeKey]: e.target.value
                                                            }));
                                                        }}
                                                        disabled={isLoadingPredefinedAttributes}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        sx={commonSx}
                                                    />
                                                );

                                            case 'NUMBER':
                                                return (
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label={attributeKey}
                                                        value={currentValue}
                                                        onChange={(e) => {
                                                            setAttributes(prev => ({
                                                                ...prev,
                                                                [attributeKey]: e.target.value
                                                            }));
                                                        }}
                                                        disabled={isLoadingPredefinedAttributes}
                                                        sx={commonSx}
                                                    />
                                                );

                                            case 'TEXT':
                                            default:
                                                return (
                                                    <TextField
                                                        size="small"
                                                        type="text"
                                                        label={attributeKey}
                                                        value={currentValue}
                                                        onChange={(e) => {
                                                            setAttributes(prev => ({
                                                                ...prev,
                                                                [attributeKey]: e.target.value
                                                            }));
                                                        }}
                                                        disabled={isLoadingPredefinedAttributes}
                                                        sx={commonSx}
                                                    />
                                                );
                                        }
                                    };

                                    return (
                                        <Box key={attributeKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, width: 'calc(50% - 8px)' }}>
                                            {renderAttributeInput()}
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveAttribute(attributeKey)}
                                                sx={{ color: 'error.main' }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                </Box>

                                {/* Przycisk dodawania atrybutu */}
                                {getAvailableAttributes().length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => setAttributeMenuAnchor(e.currentTarget)}
                                            sx={{ 
                                                color: 'primary.main',
                                                border: '1px dashed',
                                                borderColor: 'primary.main',
                                                borderRadius: 1
                                            }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                        <Typography variant="body2" sx={{ ml: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                                            Dodaj atrybut
                                        </Typography>
                                    </Box>
                                )}

                                {/* Menu wyboru atrybutu */}
                                <Menu
                                    anchorEl={attributeMenuAnchor}
                                    open={Boolean(attributeMenuAnchor)}
                                    onClose={() => setAttributeMenuAnchor(null)}
                                >
                                    {getAvailableAttributes().map((attr) => (
                                        <MenuItem
                                            key={attr.attributeKey}
                                            onClick={() => handleAddAttribute(attr.attributeKey || '')}
                                            sx={{ fontSize: '0.85rem' }}
                                        >
                                            {attr.attributeKey}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        </Box>

                    </Stack>
                </DialogContent>

                {/* Poprawione pozycjonowanie przycisków */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        py: 1,
                        borderTop: '1px solid #eee',
                        backgroundColor: '#fafafa',
                        borderRadius: 2
                    }}
                >
                    {!lead?.id && (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={createNext}
                                        onChange={(e) => setCreateNext(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Utwórz następny"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.8rem'
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={createTask}
                                        onChange={(e) => setCreateTask(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Utwórz zadanie dla kontaktu"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.8rem'
                                    }
                                }}
                            />
                        </>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={onClose} disabled={isSubmitting}>
                            Anuluj
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
                        </Button>
                    </Box>
                </Box>
            </form>
        </Dialog>

            {/* Task Dialog */}
            {createdLead && (
                <TaskDialog
                    open={showTaskDialog}
                    onClose={handleTaskDialogClose}
                    onSave={handleTaskSave}
                    task={null}
                    preAssignedLeadId={createdLead.id}
                />
            )}
        </>
    );
};

export default LeadContactDialog;
